import { getFramePlan, getSettings } from "./core.js"
import { getCroppedOutputDimensions } from "./crop.js"

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

function joinReasons(reasons) {
  if (!reasons.length) return "current workload"
  if (reasons.length === 1) return reasons[0]
  if (reasons.length === 2) return `${reasons[0]} and ${reasons[1]}`
  return `${reasons.slice(0, -1).join(", ")}, and ${reasons.at(-1)}`
}

export function getWorkerDecision(dimensions, frameCount, settings) {
  const hardwareThreads = Math.max(1, Number(navigator.hardwareConcurrency) || 2)
  const deviceMemoryGb = Math.max(1, Number(navigator.deviceMemory) || 4)
  const pixels = Math.max(1, dimensions.width * dimensions.height)
  const cpuLimit = Math.max(1, hardwareThreads - 1)
  const platformLimit = deviceMemoryGb <= 2 ? 2 : deviceMemoryGb <= 4 ? 4 : 8
  const buffersPerJob = settings.morphFrames > 0 ? 8 : 5
  const estimatedPerWorker = Math.max(16 * 1024 ** 2, pixels * 4 * buffersPerJob)
  const memoryBudget = deviceMemoryGb * 1024 ** 3 * 0.18
  const memoryLimit = Math.max(1, Math.floor(memoryBudget / estimatedPerWorker))
  const formatLimit = settings.format === "webp" && pixels > 1_000_000 ? 4 : 8
  const pixelLoad = Math.max(0.35, pixels / 250_000)
  const morphLoad = 1 + settings.morphFrames * 0.12
  const formatLoad = settings.format === "webp" ? 1.15 : 1
  const workloadLimit = Math.max(1, Math.floor(cpuLimit / Math.sqrt(pixelLoad * morphLoad * formatLoad)))
  const frameLimit = Math.max(1, frameCount)
  const limits = [
    ["CPU reserve", cpuLimit],
    ["reported device memory", platformLimit],
    ["working-memory estimate", memoryLimit],
    ["output format", formatLimit],
    ["frame-size and morphing workload", workloadLimit],
    ["number of output frames", frameLimit],
  ]
  const safeMax = clamp(Math.min(...limits.map(([, value]) => value)), 1, 8)
  const limitingReasons = limits.filter(([, value]) => value === safeMax).map(([name]) => name)
  const select = document.getElementById("workerMode")
  const requested = select?.value || "auto"
  const manual = requested !== "auto"
  const count = manual ? clamp(Number(requested) || 1, 1, 8) : Math.max(1, Math.min(safeMax, Math.ceil(safeMax / 2)))
  const reason = manual
    ? `Manual ${count}. Auto would adapt between 1 and ${safeMax}, limited by ${joinReasons(limitingReasons)}. Memory safety checks still apply.`
    : `Auto starts with ${count} and may adapt live between 1 and ${safeMax}. Safe maximum is limited by ${joinReasons(limitingReasons)}.`

  return {
    count,
    startCount: count,
    safeMax,
    minCount: 1,
    manual,
    reason,
    hardwareThreads,
    deviceMemoryGb,
    limitingReasons,
  }
}

export function updateLiveWorkerStatus(status) {
  const value = document.getElementById("workerValue")
  const reason = document.getElementById("workerReason")
  const pill = document.getElementById("workerPill")
  if (!value || !reason || !pill) return
  const mode = status.manual ? "manual" : "auto"
  value.textContent = `${status.count} ${mode}`
  pill.textContent = `${status.manual ? "Manual" : "Auto live"} · ${status.count} worker${status.count === 1 ? "" : "s"}`
  const throughput = Number.isFinite(status.throughput) && status.throughput > 0 ? ` · ${status.throughput.toFixed(1)} frames/s` : ""
  reason.textContent = `${status.reason}${throughput}`
}

export function refreshWorkerControl() {
  const settings = getSettings()
  const dimensions = getCroppedOutputDimensions(settings)
  const frameCount = getFramePlan(settings).totalFrames
  const decision = getWorkerDecision(dimensions, frameCount, settings)
  updateLiveWorkerStatus({
    count: decision.count,
    manual: decision.manual,
    reason: decision.reason,
    throughput: 0,
  })
  return decision
}

export function setupWorkerControls() {
  const select = document.getElementById("workerMode")
  if (!select) return
  const refresh = () => refreshWorkerControl()
  select.addEventListener("input", refresh)
  select.addEventListener("change", refresh)
  addEventListener("converter-plan-change", refresh)
  addEventListener("converter-crop-change", refresh)
  addEventListener("resize", refresh)
  refresh()
}
